import { FunctionComponent, useEffect } from 'react'
import { Routes, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import '@/styles/_app.scss'
import { Device, DeviceInfo } from '@capacitor/device'
import { initPushNotifications } from '@/modules/push'
import { RouteConfig } from '@/modules/routes'
import Nav from '@/components/Nav/Nav'
import { Role } from '@/types/Role'
import DownloadMobileApp from '@/components/DownloadMobileApp/DownloadMobileApp'
import Version from '@/components/Version/Version'
import { LocalizationProvider } from '@mui/x-date-pickers'
import DeepLink from '@/components/DeepLink'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { InAppReview } from '@capacitor-community/in-app-review'

const FRONTEND_VERSION: string = __APP_VERSION__

const App: FunctionComponent = () => {
    const { user, device, apiVersion, setDevice, setPushToken } = useAuth()

    const [searchParams] = useSearchParams()

    useEffect((): void => {
        const requestDaycare: string | null = searchParams.get('requestDaycare')
        if (requestDaycare) localStorage.setItem('requestDaycare', requestDaycare)
    }, [])

    useEffect(() => {
        Device.getInfo().then((deviceInfos: DeviceInfo): void => {
            const isVirtualAppleDevice: boolean = deviceInfos.platform === 'ios' && deviceInfos.isVirtual

            if (deviceInfos.platform !== 'web' && !isVirtualAppleDevice) {
                initPushNotifications(setPushToken)
                    .then(() => setDevice(deviceInfos))
                    .catch(() => setDevice(deviceInfos))
            } else {
                setPushToken(undefined)
                setDevice(deviceInfos)
            }
        })
    }, [Device])

    useEffect(() => {
        if (device && user && apiVersion !== '') {
            SplashScreen.hide()
        }
        if (device?.platform === 'ios') {
            StatusBar.setStyle({ style: Style.Light })
        }

        if ((device?.platform === 'android' || device?.platform === 'ios') && user?.email) {
            InAppReview.requestReview()
        }
    }, [device, user])

    SplashScreen.hide()

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getLocalFromLanguage()}>
            {device?.platform === 'web' &&
                (device.operatingSystem === 'ios' || device.operatingSystem === 'android') && (
                    <DownloadMobileApp device={device} isAuth={user !== null} />
                )}
            <DeepLink />
            <Version minVersion={FRONTEND_VERSION} />
            <Nav />
            <main className={`${user?.role === Role.parent ? 'backgroundParent' : ''}`}>
                <div className={'view'}>
                    <Routes>{RouteConfig({ frontendVersion: FRONTEND_VERSION })}</Routes>
                </div>
            </main>
        </LocalizationProvider>
    )
}

export default App
